import axios from 'axios';

export function initialize(store, router) {

    // ---------- CONFIG ----------
    const INACTIVITY_LIMIT_MINUTES = 30; // tiempo de inactividad permitido (cambiar si hace falta)
    const INACTIVITY_LIMIT_MS = INACTIVITY_LIMIT_MINUTES * 60 * 1000;
    const WARNING_MINUTES = 25; // Mostrar alerta a los 25 minutos
    const WARNING_MS = WARNING_MINUTES * 60 * 1000;
    const GRACE_PERIOD_MS = 5 * 60 * 1000; // 5 minutos de gracia después de la alerta
    const LAST_ACTIVITY_KEY = 'app_last_activity_ts';
    const LOGOUT_KEY = 'app_logout';
    const TOKEN_KEY = 'token';
    const REFRESH_TOKEN_KEY = 'refresh_token';

    // ---------- helpers ----------
    function safeNavigate(path) {
        try {
            const cur = router?.currentRoute?.value ?? router?.currentRoute;
            const curPath = cur?.fullPath ?? cur?.path;
            if (curPath !== path) router.push(path).catch(()=>{});
        } catch (e) {
            try { router.push(path).catch(()=>{}); } catch(e) {}
        }
    }

    function setAuthHeader(token) {
        if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        else delete axios.defaults.headers.common['Authorization'];
    }

    // sincronizar store desde localStorage al iniciar (importante para ventanas nuevas)
    try {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (storedToken) {
            try { if (store.commit) store.commit('setToken', storedToken); } catch(e) {}
            setAuthHeader(storedToken);
        }
        if (storedRefresh) {
            try { if (store.commit) store.commit('setRefreshToken', storedRefresh); } catch(e) {}
        }
    } catch (e) { /* silent */ }

    // ---------- actividad / inactividad ----------
    let inactivityTimer = null;
    let warningTimer = null;
    let graceTimer = null;
    let isWarningActive = false;
    let warningModal = null;

    function updateLastActivity(now = Date.now()) {
        try {
            localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
        } catch (e) {}
        
        // Si hay una alerta activa, cerrarla
        if (isWarningActive) {
            dismissInactivityWarning();
        }
        
        resetInactivityTimers();
    }

    function readLastActivity() {
        try {
            const v = localStorage.getItem(LAST_ACTIVITY_KEY);
            return v ? Number(v) : Date.now();
        } catch (e) {
            return Date.now();
        }
    }

    function resetInactivityTimers() {
        // Limpiar todos los timers
        if (inactivityTimer) {
            clearTimeout(inactivityTimer);
            inactivityTimer = null;
        }
        if (warningTimer) {
            clearTimeout(warningTimer);
            warningTimer = null;
        }
        if (graceTimer) {
            clearTimeout(graceTimer);
            graceTimer = null;
        }

        const last = readLastActivity();
        const now = Date.now();
        const elapsed = now - last;
        
        // Verificar si ya expiró la sesión (más de 30 minutos)
        if (elapsed >= INACTIVITY_LIMIT_MS) {
            handleInactivityExpired();
            return;
        }

        // Configurar timer para la alerta (25 minutos)
        const warningRemaining = Math.max(0, WARNING_MS - elapsed);
        if (warningRemaining > 0) {
            warningTimer = setTimeout(() => {
                showInactivityWarning();
            }, warningRemaining);
        } else {
            // Ya pasaron los 25 minutos, mostrar alerta inmediatamente si no está activa
            if (!isWarningActive) {
                showInactivityWarning();
            }
        }

        // Configurar timer para logout final (30 minutos)
        const logoutRemaining = Math.max(0, INACTIVITY_LIMIT_MS - elapsed);
        inactivityTimer = setTimeout(() => {
            handleInactivityExpired();
        }, logoutRemaining);
    }

    function showInactivityWarning() {
        // No mostrar alerta en FullScreen
        const currentRoute = router?.currentRoute?.value ?? router?.currentRoute;
        const routeName = currentRoute?.name;
        if (routeName === 'Inicio-Fullscreen') {
            return;
        }

        isWarningActive = true;
        
        if (window.Swal) {
            warningModal = window.Swal.fire({
                title: '⚠️ Sesión por expirar',
                html: `
                    <div style="text-align: center;">
                        <p style="font-size: 1.1rem; margin-bottom: 1rem;">
                            Su sesión expirará en <strong id="countdown">5:00</strong> por inactividad.
                        </p>
                        <p style="color: #666; font-size: 0.9rem;">
                            ¿Desea continuar con su sesión?
                        </p>
                    </div>
                `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, continuar',
                cancelButtonText: 'Cerrar sesión',
                confirmButtonColor: '#28a745',
                cancelButtonColor: '#dc3545',
                allowOutsideClick: false,
                allowEscapeKey: false,
                timer: GRACE_PERIOD_MS,
                timerProgressBar: true,
                didOpen: () => {
                    startCountdown();
                }
            }).then((result) => {
                isWarningActive = false;
                if (result.isConfirmed) {
                    // Usuario confirmó, extender sesión
                    updateLastActivity();
                } else {
                    // Usuario canceló o expiró el tiempo
                    handleInactivityExpired();
                }
            });
        } else {
            // Fallback sin SweetAlert
            const confirmed = confirm('Su sesión expirará en 5 minutos por inactividad. ¿Desea continuar?');
            isWarningActive = false;
            if (confirmed) {
                updateLastActivity();
            } else {
                handleInactivityExpired();
            }
        }

        // Timer de gracia por si el usuario no responde
        graceTimer = setTimeout(() => {
            if (isWarningActive) {
                isWarningActive = false;
                if (warningModal) {
                    warningModal.close();
                }
                handleInactivityExpired();
            }
        }, GRACE_PERIOD_MS);
    }

    function startCountdown() {
        let timeLeft = GRACE_PERIOD_MS / 1000; // 5 minutos en segundos
        
        const countdownElement = document.getElementById('countdown');
        if (!countdownElement) return;

        const countdownInterval = setInterval(() => {
            const minutes = Math.floor(timeLeft / 60);
            const seconds = timeLeft % 60;
            countdownElement.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
            
            timeLeft--;
            
            if (timeLeft < 0 || !isWarningActive) {
                clearInterval(countdownInterval);
            }
        }, 1000);
    }

    function dismissInactivityWarning() {
        isWarningActive = false;
        if (warningModal) {
            warningModal.close();
            warningModal = null;
        }
        if (graceTimer) {
            clearTimeout(graceTimer);
            graceTimer = null;
        }
    }

    function handleInactivityExpired() {
        // console.log('🚪 Sesión expirada por inactividad');
        
        // Cerrar cualquier alerta activa
        if (isWarningActive) {
            dismissInactivityWarning();
        }
        
        try { localStorage.setItem(LOGOUT_KEY, Date.now().toString()); } catch (e) {}
        try { if (store.commit) store.commit('logout'); } catch(e) {}
        setAuthHeader(null);
        
        // Mostrar mensaje de sesión expirada
        if (window.Swal) {
            window.Swal.fire({
                title: 'Sesión expirada',
                text: 'Su sesión ha expirado por inactividad. Por favor, inicie sesión nuevamente.',
                icon: 'info',
                confirmButtonText: 'Aceptar',
                allowOutsideClick: false,
                allowEscapeKey: false
            }).then(() => {
                safeNavigate('/');
            });
        } else {
            alert('Su sesión ha expirado por inactividad. Por favor, inicie sesión nuevamente.');
            safeNavigate('/');
        }
    }

    // ---------- Validación de sesión al acceder a rutas ----------
    function validateSessionOnRouteAccess() {
        const last = readLastActivity();
        const now = Date.now();
        const elapsed = now - last;
        
        // Si han pasado más de 30 minutos desde la última actividad
        if (elapsed >= INACTIVITY_LIMIT_MS) {
            // console.log('🚪 Sesión inválida detectada al acceder a ruta');
            handleInactivityExpired();
            return false;
        }
        
        return true;
    }

    // escuchar actividad del usuario en esta ventana
    const activityEvents = ['mousemove','mousedown','keydown','touchstart','click','scroll'];
    function activityHandler() { updateLastActivity(); }

    function addActivityListeners() {
        activityEvents.forEach(ev => window.addEventListener(ev, activityHandler, { passive: true }));
        // detectar cambios de vista
        try { 
            if (router && router.afterEach) {
                router.afterEach(() => updateLastActivity());
            }
        } catch(e) {}

        try {
        if (router && router.beforeEach) {
            router.beforeEach((to, from, next) => {
                try {
                    // ✅ 1. VALIDACIÓN DE SESIÓN: Verificar sesión válida al acceder a rutas protegidas
                    const requiresAuth = to.matched.some(r => r.meta && r.meta.requiresAuth);
                    const isLoginRoute = to.path === '/' || to.path === '/ingresar';
                    const isFullScreenRoute = to.name === 'Inicio-Fullscreen';
                    
                    // Solo validar sesión si la ruta requiere autenticación y no es login o fullscreen
                    if (requiresAuth && !isLoginRoute && !isFullScreenRoute) {
                        // Verificar si hay token
                        const hasToken = store.state.user?.token || localStorage.getItem(TOKEN_KEY);
                        if (!hasToken) {
                            // console.log('🚪 No hay token válido, redirigiendo a login');
                            return next('/');
                        }
                        
                        // Verificar validez de la sesión por tiempo de inactividad
                        if (!validateSessionOnRouteAccess()) {
                            // La validación ya maneja el logout y redirección
                            return next('/');
                        }
                    }

                    // ✅ 2. VALIDACIÓN DE PERMISOS: comprobar si la ruta requiere superadmin
                    const requiresSuper = to.matched.some(r => r.meta && r.meta.superAdmin)
                        || (to.path && to.path.startsWith('/users'));

                    if (requiresSuper) {
                        const roleId = Number(store.state.user?.role_id || store.state.user?.role?.id || 0);
                        if (roleId !== 1) {
                            // no autorizado
                            try { if (window.Swal) window.Swal.fire({ icon: 'warning', title: 'Acceso denegado', text: 'Se requieren privilegios de superadministrador.' }); } catch(e) {}
                            // redirigir a página principal (evita bucles)
                            return next({ path: '/inicio' });
                        }
                    }

                    return next();
                } catch (e) {
                    console.error('Error in beforeEach guard:', e);
                    return next();
                }
            });
        }
    } catch (e) {}
    }

    function removeActivityListeners() {
        activityEvents.forEach(ev => window.removeEventListener(ev, activityHandler));
    }

    // si quieres que peticiones automáticas también cuenten como actividad,
    // el interceptor de respuesta abajo llamará updateLastActivity() en respuestas 2xx.
    // ---------- sincronización entre ventanas ----------
    window.addEventListener('storage', (e) => {
        if (!e.key) return;
        if (e.key === LAST_ACTIVITY_KEY) {
            // otra ventana actualizó actividad -> resetear timers aquí
            if (isWarningActive) {
                dismissInactivityWarning();
            }
            resetInactivityTimers();
        } else if (e.key === LOGOUT_KEY) {
            // otra ventana forzó logout: aplicar localmente
            if (isWarningActive) {
                dismissInactivityWarning();
            }
            try { if (store.commit) store.commit('logout'); } catch(e) {}
            setAuthHeader(null);
            safeNavigate('/');
        } else if (e.key === TOKEN_KEY) {
            const newToken = localStorage.getItem(TOKEN_KEY);
            if (newToken) {
                try { if (store.commit) store.commit('setToken', newToken); } catch(e) {}
                setAuthHeader(newToken);
            } else {
                setAuthHeader(null);
            }
        }
    });

    // ---------- AXIOS INTERCEPTORS ----------
    let reqInterceptorId = null;
    let respInterceptorId = null;

    reqInterceptorId = axios.interceptors.request.use((config) => {
        // adjuntar token desde localStorage (para que nuevas ventanas cojan el token)
        const token = store.state.user?.token || localStorage.getItem(TOKEN_KEY) || '';
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        } else if (config.headers) {
            delete config.headers.Authorization;
        }
        // registrar actividad (una petición saliente se considera actividad)
        updateLastActivity();
        return config;
    }, (err) => Promise.reject(err));

    respInterceptorId = axios.interceptors.response.use((response) => {
        // respuesta exitosa -> actividad
        updateLastActivity();
        return response;
    }, (error) => {
        // Si recibimos 401: no intentar refresh automático. Forzar logout global.
        const status = error?.response?.status;
        if (status === 401) {
            try { localStorage.setItem(LOGOUT_KEY, Date.now().toString()); } catch (e) {}
            try { if (store.commit) store.commit('logout'); } catch(e) {}
            setAuthHeader(null);
            safeNavigate('/');
        }
        return Promise.reject(error);
    });

    // ---------- STORE SUBSCRIBE para persistir tokens y limpiar ----------
    store.subscribe((mutation) => {
        if (mutation.type === 'logout') {
            // limpiar timers, interceptores y storage
            if (inactivityTimer) { clearTimeout(inactivityTimer); inactivityTimer = null; }
            if (warningTimer) { clearTimeout(warningTimer); warningTimer = null; }
            if (graceTimer) { clearTimeout(graceTimer); graceTimer = null; }
            if (isWarningActive) { dismissInactivityWarning(); }
            try { if (reqInterceptorId != null) axios.interceptors.request.eject(reqInterceptorId); } catch(e){}
            try { if (respInterceptorId != null) axios.interceptors.response.eject(respInterceptorId); } catch(e){}
            try { localStorage.removeItem(TOKEN_KEY); } catch(e){}
            try { localStorage.setItem(LOGOUT_KEY, Date.now().toString()); } catch(e){}
        }
        if (mutation.type === 'setUser' || mutation.type === 'login' || mutation.type === 'setToken') {
            const newToken = store.state.user?.token || localStorage.getItem(TOKEN_KEY);
            if (newToken) {
                try { localStorage.setItem(TOKEN_KEY, newToken); } catch(e){}
                setAuthHeader(newToken);
            }
            // persistir refresh_token si viene en store
            try {
                const rt = store.state.user?.refresh_token;
                if (rt) localStorage.setItem(REFRESH_TOKEN_KEY, rt);
            } catch (e) {}
            // resetear timers de inactividad
            updateLastActivity();
        }
    });

    // ---------- INICIALIZACIÓN ----------
    addActivityListeners();
    // si no hay clave LAST_ACTIVITY_KEY inicial, poner ahora
    if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
        updateLastActivity();
    } else {
        resetInactivityTimers();
    }

    // Exponer utilidades para que otras partes del front puedan usar
    window.__APP_RECORD_ACTIVITY = updateLastActivity;
    
    // Función para limpiar todos los recursos (útil al destruir la app)
    window.__APP_CLEANUP_SESSION = function() {
        // console.log('🧹 Limpiando recursos de sesión...');
        if (inactivityTimer) { clearTimeout(inactivityTimer); inactivityTimer = null; }
        if (warningTimer) { clearTimeout(warningTimer); warningTimer = null; }
        if (graceTimer) { clearTimeout(graceTimer); graceTimer = null; }
        if (isWarningActive) { dismissInactivityWarning(); }
        removeActivityListeners();
        detenerVerificacionUnidades();
        try { if (reqInterceptorId != null) axios.interceptors.request.eject(reqInterceptorId); } catch(e){}
        try { if (respInterceptorId != null) axios.interceptors.response.eject(respInterceptorId); } catch(e){}
    };

    // ---------- VERIFICACIÓN GLOBAL DE UNIDADES DESCONECTADAS ----------
    let verificacionInterval = null;
    const VERIFICACION_INTERVAL_MS = 2 * 60 * 1000; // 2 minutos

    async function verificarUnidadesDesconectadas() {
        if (!store.state.user || !store.state.user.token) return;
        
        try {
            const response = await axios.get('/api/verificar-unidades-desconectadas', {
                headers: { Authorization: `Bearer ${store.state.user.token}` }
            });
            // console.log('🔍 Verificación de unidades desconectadas:', response.data);
        } catch (error) {
            // Silencioso para evitar spam en consola
            // console.error('Error verificando unidades desconectadas:', error);
        }
    }

    function iniciarVerificacionUnidades() {
        if (verificacionInterval) {
            clearInterval(verificacionInterval);
        }
        
        // Ejecutar inmediatamente una vez
        verificarUnidadesDesconectadas();
        
        // Luego cada 2 minutos
        verificacionInterval = setInterval(verificarUnidadesDesconectadas, VERIFICACION_INTERVAL_MS);
    }

    function detenerVerificacionUnidades() {
        if (verificacionInterval) {
            clearInterval(verificacionInterval);
            verificacionInterval = null;
        }
    }

    // Iniciar verificación cuando hay usuario autenticado
    if (store.state.user && store.state.user.token) {
        iniciarVerificacionUnidades();
    }

    // Escuchar cambios en el store para iniciar/detener verificación
    store.subscribe((mutation) => {
        if (mutation.type === 'logout') {
            detenerVerificacionUnidades();
        }
        if (mutation.type === 'loginSuccess' || mutation.type === 'setToken') {
            iniciarVerificacionUnidades();
        }
    });
}